import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getCalendarActivities, createCalendarActivity } from '../../../utils/services/user.service';
import Seg from './components/Seg';
import Icon from './components/Icon';
import CalendarNavigator from './components/CalendarNavigator';
import CalendarView from './components/CalendarView';
import TimelineView from './components/TimelineView';
import SelectedDayPanel from './components/SelectedDayPanel';
import QuickInsights from './components/QuickInsights';
import AddActivityModal from './components/AddActivityModal';
import ActivityDetailModal from './components/ActivityDetailModal';
import {
  TZ,
  DEFAULT_TYPE_FILTER,
  mapApiItemToActivity,
  monthName,
  startOfMonth,
  endOfMonth,
  addMonths,
  addYears,
  fmtDateISO,
  fmtTime,
  safeParseDate,
  uid,
  clamp,
  withinRange,
  isSameDay,
  typeAccent,
  normalizeType,
} from './utils';
import '../calendar.css';

export default function CustodiyActivityCalendar() {
  const [viewMode, setViewMode] = useState('calendar');
  const [granularity, setGranularity] = useState('month');
  const [now] = useState(() => new Date());
  const [cursor, setCursor] = useState(() => new Date(now.getFullYear(), now.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState(() => new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  const [activities, setActivities] = useState(() => []);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState(() => ({ ...DEFAULT_TYPE_FILTER }));
  const [addOpen, setAddOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [draftType, setDraftType] = useState('UserTask');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDate, setDraftDate] = useState(() => fmtDateISO(new Date()));
  const [draftTime, setDraftTime] = useState(() => fmtTime(new Date()));
  const [draftNote, setDraftNote] = useState('');
  const [draftPriority, setDraftPriority] = useState('medium');
  const [draftInvitedUserName, setDraftInvitedUserName] = useState('');
  const [draftRemind, setDraftRemind] = useState(0);
  const [createLoading, setCreateLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [navFlash, setNavFlash] = useState(false);
  const shellRef = useRef(null);

  function flashNav() {
    setNavFlash(true);
    window.setTimeout(() => setNavFlash(false), 220);
  }

  useEffect(() => {
    const fetchCalendar = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const res = await getCalendarActivities();
        const data = res?.data?.data;
        if (Array.isArray(data)) {
          const mapped = data.map(mapApiItemToActivity);
          setActivities(mapped);
        } else {
          setActivities([]);
        }
      } catch (err) {
        setApiError(err?.response?.data?.message || err?.message || 'Failed to load calendar');
        setActivities([]);
        setToast('Could not load calendar activities');
      } finally {
        setLoading(false);
      }
    };
    fetchCalendar();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = clamp((e.clientX - r.left) / r.width, 0, 1);
      const y = clamp((e.clientY - r.top) / r.height, 0, 1);
      el.style.setProperty('--px', `${x * 100}%`);
      el.style.setProperty('--py', `${y * 100}%`);
    };
    el.addEventListener('pointermove', onMove);
    return () => el.removeEventListener('pointermove', onMove);
  }, []);

  const cursorRange = useMemo(() => {
    if (granularity === 'month') return { a: startOfMonth(cursor), b: endOfMonth(cursor) };
    const a = new Date(cursor.getFullYear(), 0, 1, 0, 0, 0, 0);
    const b = new Date(cursor.getFullYear(), 11, 31, 23, 59, 59, 999);
    return { a, b };
  }, [cursor, granularity]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return activities
      .filter((a) => typeFilter[normalizeType(a.type)])
      .filter((a) => {
        if (!qq) return true;
        const m = (a.title + ' ' + (a.note || '') + ' ' + JSON.stringify(a.meta || {})).toLowerCase();
        return m.includes(qq);
      });
  }, [activities, q, typeFilter]);

  const calendarActivities = useMemo(() => {
    return filtered.filter((a) => {
      const d = safeParseDate(a.at);
      return withinRange(d, cursorRange.a, cursorRange.b);
    });
  }, [filtered, cursorRange.a.getTime(), cursorRange.b.getTime()]);

  const selectedDayItems = useMemo(() => {
    const sd = selectedDay;
    return filtered
      .filter((a) => { const d = safeParseDate(a.at); return isSameDay(d, sd); })
      .sort((x, y) => safeParseDate(y.at).getTime() - safeParseDate(x.at).getTime());
  }, [filtered, selectedDay.getTime()]);

  const timelineItems = useMemo(() => {
    return filtered
      .filter((a) => { const d = safeParseDate(a.at); return withinRange(d, cursorRange.a, cursorRange.b); })
      .sort((x, y) => safeParseDate(y.at).getTime() - safeParseDate(x.at).getTime());
  }, [filtered, cursorRange.a.getTime(), cursorRange.b.getTime()]);

  const daysGrid = useMemo(() => {
    if (granularity !== 'month') return [];
    const start = startOfMonth(cursor);
    const startWeekday = (start.getDay() + 6) % 7;
    const gridStart = new Date(start);
    gridStart.setDate(start.getDate() - startWeekday);
    const out = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      out.push({ day: d, inMonth: d.getMonth() === cursor.getMonth() && d.getFullYear() === cursor.getFullYear() });
    }
    return out;
  }, [cursor, granularity]);

  const dayMeta = useMemo(() => {
    const map = new Map();
    calendarActivities.forEach((a) => {
      const d = safeParseDate(a.at);
      const key = fmtDateISO(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
      const prev = map.get(key) || { count: 0, dots: [] };
      prev.count += 1;
      prev.dots.push({ t: a.type, c: typeAccent(a.type) });
      map.set(key, prev);
    });
    return map;
  }, [calendarActivities]);

  const detail = useMemo(() => {
    if (!detailId) return null;
    return activities.find((a) => a.id === detailId) || null;
  }, [activities, detailId]);

  const headerTitle = useMemo(() => {
    if (granularity === 'month') return `${monthName(cursor.getMonth())} ${cursor.getFullYear()}`;
    return `${cursor.getFullYear()}`;
  }, [cursor, granularity]);

  const stats = useMemo(() => {
    const total = timelineItems.length;
    const byType = {};
    timelineItems.forEach((a) => { byType[a.type] = (byType[a.type] || 0) + 1; });
    const top = Object.entries(byType).sort((a, b) => b[1] - a[1]).slice(0, 3);
    return { total, top };
  }, [timelineItems]);

  const yearMonths = useMemo(() => {
    if (granularity !== 'year') return [];
    return Array.from({ length: 12 }).map((_, i) => new Date(cursor.getFullYear(), i, 1));
  }, [cursor, granularity]);

  const selectedLabel = fmtDateISO(selectedDay);

  function goPrev() {
    flashNav();
    setCursor((c) => (granularity === 'month' ? addMonths(c, -1) : addYears(c, -1)));
  }
  function goNext() {
    flashNav();
    setCursor((c) => (granularity === 'month' ? addMonths(c, 1) : addYears(c, 1)));
  }
  function goToday() {
    flashNav();
    const d = new Date();
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
    setSelectedDay(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    setToast('Jumped to Today');
  }
  function toggleType(t) {
    setTypeFilter((p) => ({ ...p, [t]: !p[t] }));
  }
  function quickAll(on) {
    setTypeFilter(Object.fromEntries(Object.keys(DEFAULT_TYPE_FILTER).map((k) => [k, on])));
  }
  function openAddForDay(d) {
    const base = d || selectedDay;
    setDraftType('UserTask');
    setDraftTitle('');
    setDraftNote('');
    setDraftPriority('medium');
    setDraftInvitedUserName('');
    setDraftRemind(0);
    setDraftDate(fmtDateISO(base));
    setDraftTime('09:00');
    setAddOpen(true);
  }
  async function createActivity() {
    const title = draftTitle.trim();
    if (!title) { setToast('Title required'); return; }
    setCreateLoading(true);
    try {
      const endDate = new Date(`${draftDate}T${draftTime}:00`).toISOString();
      const payload = {
        title,
        endDate,
        priority: draftPriority,
        description: draftNote.trim() || undefined,
        notification_time: draftType === 'UserTask' && draftRemind > 0 ? draftRemind : undefined,
        invited_user_name: draftInvitedUserName.trim() || undefined,
      };
      const res = await createCalendarActivity(payload);
      const created = res?.data?.data;
      if (created) {
        const mapped = mapApiItemToActivity(created);
        setActivities((p) => [mapped, ...p]);
      } else {
        const a = { id: uid('u'), title, type: 'UserTask', at: endDate, note: draftNote.trim() || undefined, createdBy: 'You', completed: false };
        setActivities((p) => [a, ...p]);
      }
      setAddOpen(false);
      setToast(draftInvitedUserName ? `Activity shared with ${draftInvitedUserName}` : 'Activity added');
      const d = safeParseDate(endDate);
      setSelectedDay(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    } catch (err) {
      setToast(err?.response?.data?.message || err?.message || 'Failed to create activity');
    } finally {
      setCreateLoading(false);
    }
  }
  function removeActivity(id) {
    setActivities((p) => p.filter((x) => x.id !== id));
    setToast('Activity removed');
    setDetailOpen(false);
  }
  function openDetail(id) {
    setDetailId(id);
    setDetailOpen(true);
  }
  function simulateSystemEvent(kind) {
    const now = new Date();
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const templates = {
      UserTask: () => ({ id: uid('sys'), title: `Reminder: ${pick(['Review KYC', 'Upload invoice', 'Check settlement', 'Reconcile wallet'])} `, type: 'UserTask', at: now.toISOString(), note: 'System-generated task.' }),
      Login: () => ({ id: uid('sys'), title: `Login – ${pick(['Rome', 'Milan', 'Berlin', 'Paris'])} • ${pick(['Chrome', 'Edge', 'Safari'])} `, type: 'Login', at: now.toISOString(), meta: { ip: `${Math.floor(20 + Math.random() * 200)}.xx.xx.xx`, risk: pick(['Low', 'Medium']), device: pick(['Windows', 'iOS', 'Android']) } }),
      Order: () => ({ id: uid('sys'), title: `Order executed – ${pick(['OTC', 'Marketplace', 'Swap'])} `, type: 'Order', at: now.toISOString(), amount: Math.round((200 + Math.random() * 5000) * 100) / 100, currency: pick(['EUR', 'USD']), meta: { status: 'Filled', orderId: `ORD-${now.getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}` } }),
      Receive: () => ({ id: uid('sys'), title: `Funds received – ${pick(['Bank Wallet', 'Virtual Wallet', 'On-chain Wallet'])} `, type: 'Receive', at: now.toISOString(), amount: Math.round((50 + Math.random() * 20000) * 100) / 100, currency: pick(['EUR', 'USD', 'USDT']), meta: { source: pick(['SEPA', 'Swift', 'On-chain']), ref: `REF-${Math.floor(10000 + Math.random() * 90000)}` } }),
      Send: () => ({ id: uid('sys'), title: `Funds sent – ${pick(['BSC', 'ETH', 'SEPA'])}`, type: 'Send', at: now.toISOString(), amount: Math.round((0.01 + Math.random() * 3) * 10000) / 10000, currency: pick(['BNB', 'ETH', 'EUR']), meta: { tx: `0x${Math.random().toString(16).slice(2, 10)}...`, network: pick(['BSC', 'ETH', 'BANK']) } }),
      PasswordChange: () => ({ id: uid('sys'), title: 'Password changed + session rotation', type: 'PasswordChange', at: now.toISOString(), meta: { method: pick(['OTP', '2FA']), result: 'Success' } }),
      Security: () => ({ id: uid('sys'), title: `Security alert – ${pick(['New device', 'Impossible travel', 'Multiple failures', 'Risk spike'])}`, type: 'Security', at: now.toISOString(), meta: { severity: pick(['Medium', 'High']), action: pick(['OTP required', '2FA enforced', 'Session revoked']) } }),
      System: () => ({ id: uid('sys'), title: `System event – ${pick(['Settlement completed', 'Audit snapshot', 'Indexing finished'])}`, type: 'System', at: now.toISOString() }),
    };
    const act = templates[kind]();
    setActivities((p) => [act, ...p]);
    setToast('System event logged');
    const d = safeParseDate(act.at);
    setSelectedDay(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
  }

  return (
    <div className="default-pages-calendar" ref={shellRef}>
      <div className="page">
        {loading && (
          <div className="calendarLoading" aria-live="polite">
            <div className="calendarLoadingSpinner" />
            <span>Loading calendar…</span>
          </div>
        )}
        {apiError && !loading && (
          <div className="calendarApiError">
            <span>{apiError}</span>
          </div>
        )}
        <div className="bg" aria-hidden>
          <div className="bgGlow" />
          <div className="bgGrid" />
        </div>

        <header className="topbar">
          <div className="brand">
            <div className="brandMark">C</div>
            <div className="brandTxt">
              <div className="brandTitle">Custodiy</div>
              <div className="brandSub">Activity Center • {TZ}</div>
            </div>
          </div>
          <div className="topActions">
            <div className="segWrap" role="tablist" aria-label="View mode">
              <Seg label="Calendar" icon="calendar" active={viewMode === 'calendar'} onClick={() => setViewMode('calendar')} />
              <Seg label="Timeline" icon="timeline" active={viewMode === 'timeline'} onClick={() => setViewMode('timeline')} />
            </div>
            <button type="button" className="primary" onClick={() => openAddForDay()} aria-label="Add activity">
              <Icon name="plus" />
              <span>Add</span>
            </button>
          </div>
        </header>

        <main className="main">
          <CalendarNavigator
            headerTitle={headerTitle}
            navFlash={navFlash}
            granularity={granularity}
            setGranularity={setGranularity}
            stats={stats}
            selectedDayItems={selectedDayItems}
            typeFilter={typeFilter}
            toggleType={toggleType}
            quickAll={quickAll}
            q={q}
            setQ={setQ}
            goPrev={goPrev}
            goNext={goNext}
            goToday={goToday}
            simulateSystemEvent={simulateSystemEvent}
            filtered={filtered}
            cursor={cursor}
          />

          <section className="center">
            {viewMode === 'calendar' ? (
              <div className="card cardFill">
                <div className="cardTop">
                  <div>
                    <div className="cardTitle">Calendar</div>
                    <div className="cardSub">Days highlight by tag • click to inspect • add tasks</div>
                  </div>
                  <div className="cardRight">
                    <div className="hint"><span className="hintDot" /><span>Neon tags = activity types</span></div>
                  </div>
                </div>
                <CalendarView
                  granularity={granularity}
                  daysGrid={daysGrid}
                  dayMeta={dayMeta}
                  selectedDay={selectedDay}
                  setSelectedDay={setSelectedDay}
                  yearMonths={yearMonths}
                  setGranularity={setGranularity}
                  setCursor={setCursor}
                  filtered={filtered}
                />
              </div>
            ) : (
              <div className="card cardFill">
                <div className="cardTop">
                  <div>
                    <div className="cardTitle">Timeline</div>
                    <div className="cardSub">List view • grouped by day • open detail</div>
                  </div>
                  <div className="cardRight">
                    <div className="range">
                      <span className="muted">Range:</span>
                      <span className="rangeBox">{fmtDateISO(cursorRange.a)}</span>
                      <span className="sep">→</span>
                      <span className="rangeBox">{fmtDateISO(cursorRange.b)}</span>
                    </div>
                  </div>
                </div>
                <TimelineView timelineItems={timelineItems} openDetail={openDetail} />
              </div>
            )}
          </section>

          <aside className="right">
            <SelectedDayPanel
              selectedLabel={selectedLabel}
              selectedDayItems={selectedDayItems}
              openAddForDay={openAddForDay}
              openDetail={openDetail}
              selectedDay={selectedDay}
            />
            <QuickInsights stats={stats} />
          </aside>
        </main>

        <AddActivityModal
          show={addOpen}
          onHide={() => setAddOpen(false)}
          draftType={draftType}
          setDraftType={setDraftType}
          draftTitle={draftTitle}
          setDraftTitle={setDraftTitle}
          draftDate={draftDate}
          setDraftDate={setDraftDate}
          draftTime={draftTime}
          setDraftTime={setDraftTime}
          draftNote={draftNote}
          setDraftNote={setDraftNote}
          draftPriority={draftPriority}
          setDraftPriority={setDraftPriority}
          draftInvitedUserName={draftInvitedUserName}
          setDraftInvitedUserName={setDraftInvitedUserName}
          draftRemind={draftRemind}
          setDraftRemind={setDraftRemind}
          createActivity={createActivity}
          createLoading={createLoading}
        />

        <ActivityDetailModal
          show={detailOpen}
          onHide={() => setDetailOpen(false)}
          detail={detail}
          removeActivity={removeActivity}
        />

        {toast ? <div className="toast">{toast}</div> : null}
      </div>
    </div>
  );
}
